import { computed, inject, Service, signal } from '@angular/core';
import { EventBusService } from '@shared';
import { isEqual } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { GeneralSettings, pickGeneralSettings } from '../../models/general-settings.model';
import { OrderSettings, pickOrderSettings } from '../../models/order-settings.model';
import {
	DEFAULT_KALA_USER_SETTING,
	KALA_GENERAL_SETTING_API_KEY,
	KALA_MARKET_SETTING_API_KEY,
	KALA_ORDER_SETTING_API_KEY,
	KALA_PORTFOLIO_SETTING_API_KEY,
	KALA_THEME_SETTING_API_KEY,
	KALA_WALLET_SETTING_API_KEY,
	KalaTheme,
	LEGACY_KALA_USER_SETTING_STORAGE_KEY,
	KalaUserSetting,
	MarketSettings,
	PortfolioPriceBasis,
	PortfolioSettings,
	PortfolioSortCriterion,
	PortfolioSortDirection,
	pickMarketSettings,
	pickPortfolioSettings,
	pickTheme,
	pickWalletSettings,
	WalletSettings
} from '../../models/user-setting.model';
import { KalaWalletKey } from '../../models/wallet.model';
import { UserSettingsService } from '../../services/user-settings.service';
import { USER_SETTING_EVENTS } from './user-setting-events.model';

/** The setting groups, each stored under its own `/userSettings` key. */
type SettingGroup = 'order' | 'theme' | 'portfolio' | 'market' | 'general' | 'wallet';

const API_KEY_BY_GROUP: Record<SettingGroup, string> = {
	order: KALA_ORDER_SETTING_API_KEY,
	theme: KALA_THEME_SETTING_API_KEY,
	portfolio: KALA_PORTFOLIO_SETTING_API_KEY,
	market: KALA_MARKET_SETTING_API_KEY,
	general: KALA_GENERAL_SETTING_API_KEY,
	wallet: KALA_WALLET_SETTING_API_KEY
};

const ALL_GROUPS = Object.keys(API_KEY_BY_GROUP) as SettingGroup[];

/**
 * The groups the تنظیمات page reads and writes. `market` is absent: the pinned
 * symbols are a market-page concern and the settings page never shows them.
 */
const APP_SETTING_GROUPS: readonly SettingGroup[] = ['general', 'order', 'portfolio', 'theme'];

/** What {@link UserSettingEventProducerService.saveAppSettings} is handed. */
export interface AppSettingsPatch {
	theme: KalaTheme;
	general: GeneralSettings;
	order: OrderSettings;
	priceBasis: PortfolioPriceBasis;
}

/**
 * How a write changes the settings, applied when the write reaches the front of
 * the queue — see {@link UserSettingEventProducerService.commit}.
 */
type SettingPatch = (current: KalaUserSetting) => KalaUserSetting;

/**
 * The record stored under a group's key — what the API is sent and what the
 * cache holds, so both sides speak the same shape. The order settings are the
 * record; the theme is wrapped, since a record is an object.
 */
function toRecord(setting: KalaUserSetting, group: SettingGroup): unknown {
	switch (group) {
		case 'order':
			return setting.order;
		case 'portfolio':
			return setting.portfolio;
		case 'market':
			return setting.market;
		case 'general':
			return setting.general;
		case 'wallet':
			return setting.wallet;
		case 'theme':
			// The theme is a bare value, so it is wrapped — a record is an object.
			return { theme: setting.theme };
	}
}

/** The records a multi-group write sends, keyed as `/userSettings` keys them. */
function toRecords(setting: KalaUserSetting, groups: readonly SettingGroup[]): Record<string, unknown> {
	return Object.fromEntries(groups.map(group => [API_KEY_BY_GROUP[group], toRecord(setting, group)]));
}

/**
 * Owns kala-mobile user preferences: reads and writes them through
 * `/userSettings` and publishes them on the event bus for consumers across
 * the app.
 *
 * The API is the source of truth; localStorage is only a mirror of the last
 * answer it gave — one entry per group, keyed and shaped exactly like the
 * remote record, so the settings sheet can open with the last known values
 * and still work while the API is unreachable. The cache never outranks the
 * API: it is overwritten by every successful read, and only a read that
 * *fails* leaves it standing. Nothing else writes to it — defaults are never
 * cached, so an empty cache keeps meaning "the user has saved nothing yet".
 *
 * Every write goes through one path ({@link commit}): the API first, then the
 * cache and the event bus together. So the cache can never claim a value the
 * API rejected, and any write — a full replace, a partial merge, or the order
 * sheet saving — leaves storage, application state and subscribers in step.
 *
 * Writes queue rather than overlap. Only one is in flight at a time, and the
 * groups share that queue: a pin saved while the theme is still saving waits
 * its turn instead of being refused, so no caller loses a change it reported as
 * applied. Each write computes the record it sends when its turn comes, not
 * when it was requested, so a queued write publishes the *current* value of the
 * groups it does not touch rather than the snapshot it was queued with.
 *
 * Also owns order-settings persistence (formerly `OrderSettingsStore`), so all
 * setting reads/writes live in one place. The order-settings sheet's
 * in-progress edits live in its own Signal Form model, so a cancelled or
 * dismissed sheet cannot touch {@link orderSettings} — nothing here changes
 * until {@link saveOrderSettings} succeeds.
 *
 * Prefer this service for all setting writes. Read via {@link UserSettingEventConsumerService}.
 */
@Service()
export class UserSettingEventProducerService {
	private readonly eventBus = inject(EventBusService);
	private readonly userSettings = inject(UserSettingsService);

	private readonly persisted = signal<KalaUserSetting>(this.readFromStorage());
	private readonly loadInFlight = signal(false);
	/** Writes requested and not yet settled — queued ones included. */
	private readonly pendingWrites = signal(0);
	private readonly saveErrorMsg = signal<string | null>(null);

	/** Tail of the write queue; see the note on queuing in the class comment. */
	private writes: Promise<unknown> = Promise.resolve();

	/** Order settings currently persisted — the value the order-settings sheet should read. */
	readonly orderSettings = computed(() => this.persisted().order);
	/** Theme currently persisted — what the سایر page toggles and the app renders. */
	readonly theme = computed(() => this.persisted().theme);
	/** Portfolio preferences currently persisted — the eye toggle above the holdings. */
	readonly portfolioSettings = computed(() => this.persisted().portfolio);
	/** تنظیمات عمومی currently persisted — what the settings page's first section opens on. */
	readonly generalSettings = computed(() => this.persisted().general);
	/** کیف پول preferences currently persisted — which wallet the کیف پول tab opens on. */
	readonly walletSettings = computed(() => this.persisted().wallet);
	readonly loadingOrderSettings = this.loadInFlight.asReadonly();
	readonly savingOrderSettings = computed(() => this.pendingWrites() > 0);
	readonly orderSaveError = this.saveErrorMsg.asReadonly();

	/**
	 * The same two flags under names that do not claim to be about orders — the write
	 * queue and the last error are one per service, not one per group, and the تنظیمات
	 * page drives all four groups at once.
	 */
	readonly savingSettings = this.savingOrderSettings;
	readonly saveError = this.orderSaveError;

	constructor() {
		this.dropLegacyCache();
		this.publishCurrent();
	}

	/**
	 * Merge partial preferences, persist, and broadcast.
	 *
	 * Merges against a fresh storage read so a write from another tab is not
	 * clobbered by this one's stale in-memory copy.
	 *
	 * @returns `true` when the settings were persisted, `false` when the write failed.
	 */
	updateSetting(partial: Partial<KalaUserSetting>): Promise<boolean> {
		return this.commitAll(() => ({ ...this.readFromStorage(), ...partial }));
	}

	/**
	 * Replace the full setting object, persist, and broadcast.
	 *
	 * @returns `true` when the settings were persisted, `false` when the write failed.
	 */
	setSetting(setting: KalaUserSetting): Promise<boolean> {
		return this.commitAll(() => ({ ...DEFAULT_KALA_USER_SETTING, ...setting }));
	}

	/**
	 * Re-read the cache and publish (e.g. after another tab wrote to storage).
	 * Read-only towards the API — this reports what is stored, it does not save.
	 */
	publishCurrent(): void {
		this.publish(this.readFromStorage());
	}

	/**
	 * Pull every group from the API and bring the cache, application state and
	 * subscribers in line with it. Called at startup once the user is
	 * authenticated.
	 */
	refresh(): Promise<KalaUserSetting> {
		return this.load(ALL_GROUPS);
	}

	/**
	 * Read the order settings, refreshed from the API. Safe to call every time
	 * the sheet opens.
	 *
	 * Reads the order-settings record only: the theme is a separate key that
	 * the sheet neither shows nor changes, so opening it costs one request, not
	 * one per group.
	 */
	async loadOrderSettings(): Promise<OrderSettings> {
		const setting = await this.load(['order']);
		return setting.order;
	}

	/**
	 * Read every group the تنظیمات page shows, refreshed from the API. Safe to call
	 * each time the page opens.
	 *
	 * The pinned symbols are left out: the page neither shows nor changes them, so
	 * opening it costs four requests rather than five.
	 */
	loadAppSettings(): Promise<KalaUserSetting> {
		return this.load(APP_SETTING_GROUPS);
	}

	/**
	 * Persist everything the تنظیمات page edits, in one write.
	 *
	 * Scenario 5: the page's ذخیره is the only thing that promotes its edits, and it
	 * promotes all of them together — a trader who changed the theme and the default
	 * buy quantity in one visit must not end up with one saved and the other not. The
	 * four records still go up under their own keys, but they go up in one
	 * `/userSettings/batch` request, so a rejection leaves every group as it was.
	 *
	 * Merged into each group rather than replacing it: the portfolio record also
	 * carries the eye toggle and the holdings' sort, neither of which this page shows,
	 * and a write that dropped the fields it does not touch would reset them.
	 *
	 * @returns `true` when the settings were persisted, `false` when the write failed.
	 */
	saveAppSettings(settings: AppSettingsPatch): Promise<boolean> {
		return this.commit(
			current => ({
				...current,
				theme: pickTheme(settings.theme),
				general: pickGeneralSettings(settings.general),
				order: pickOrderSettings(settings.order),
				portfolio: { ...current.portfolio, priceBasis: settings.priceBasis }
			}),
			APP_SETTING_GROUPS
		);
	}

	/**
	 * Read the given groups and fold the answers into cache, application state
	 * and subscribers.
	 *
	 * Whatever the API answers wins, including an empty answer: a key the server
	 * does not have means the user has saved nothing for that group, so it falls
	 * back to the defaults. Groups that were not read keep the value they
	 * already had. Only a *failed* call — offline, 5xx, a rejected envelope —
	 * leaves the cached copy standing, so the app stays usable without a network.
	 */
	private async load(groups: readonly SettingGroup[]): Promise<KalaUserSetting> {
		const cached = this.readFromStorage();
		this.persisted.set(cached);
		this.saveErrorMsg.set(null);
		this.loadInFlight.set(true);

		try {
			// One request per group, in parallel: they are separate records and
			// neither should wait on the other.
			const answers = await Promise.all(
				groups.map(group => firstValueFrom(this.userSettings.get<unknown>(API_KEY_BY_GROUP[group])))
			);

			const next = groups.reduce<KalaUserSetting>(
				(setting, group, index) => this.applyGroup(setting, group, answers[index]),
				cached
			);

			// The cache mirrors what is worth mirroring: settings that differ from
			// the defaults. Landing back on the defaults means the user has saved
			// nothing the API kept, and an empty cache is how we say that.
			if (isEqual(next, this.canonicalise({}))) {
				this.clearCache();
			} else {
				this.cache(next);
			}

			this.publish(next);
		} catch {
			// Offline or a failing API: the cached settings are the best answer we have.
		} finally {
			this.loadInFlight.set(false);
		}

		return this.persisted();
	}

	/** Fold one group's remote record in; a record the server does not have reads as the default. */
	private applyGroup(setting: KalaUserSetting, group: SettingGroup, remote: unknown): KalaUserSetting {
		switch (group) {
			case 'order':
				return { ...setting, order: pickOrderSettings(remote as Partial<OrderSettings> | null) };
			case 'portfolio':
				return { ...setting, portfolio: pickPortfolioSettings(remote as Partial<PortfolioSettings> | null) };
			case 'market':
				return { ...setting, market: pickMarketSettings(remote as Partial<MarketSettings> | null) };
			case 'general':
				return { ...setting, general: pickGeneralSettings(remote as Partial<GeneralSettings> | null) };
			case 'wallet':
				return { ...setting, wallet: pickWalletSettings(remote as Partial<WalletSettings> | null) };
			case 'theme': {
				// The theme record is `{ theme }`; records written before it was wrapped
				// in an object are the bare theme name.
				const record = remote as { theme?: KalaTheme } | KalaTheme | null;
				return { ...setting, theme: pickTheme(typeof record === 'string' ? record : record?.theme) };
			}
		}
	}

	/** Clear a stale save error, e.g. when the user resumes editing. */
	clearOrderSaveError(): void {
		this.saveErrorMsg.set(null);
	}

	/**
	 * Persist the given order settings and promote them to application state.
	 *
	 * Validation belongs to the form — this only guards the write itself.
	 *
	 * @returns `true` when the settings were persisted, `false` when the write failed.
	 */
	saveOrderSettings(order: OrderSettings): Promise<boolean> {
		return this.commit(current => ({ ...current, order }), ['order']);
	}

	/**
	 * Persist the theme the سایر page toggled to and promote it to application
	 * state, so every consumer — and the next session — sees it.
	 *
	 * Only the theme record is written: the order settings are a separate key
	 * and have no business being re-sent because the user changed the look.
	 *
	 * @returns `true` when the theme was persisted, `false` when the write failed.
	 */
	saveTheme(theme: KalaTheme): Promise<boolean> {
		return this.commit(current => ({ ...current, theme: pickTheme(theme) }), ['theme']);
	}

	/**
	 * Persist the portfolio's eye toggle, so hidden balances stay hidden on the
	 * next visit and on the trader's other devices.
	 *
	 * Only the portfolio record is written — the order settings and the theme are
	 * separate keys with no stake in this.
	 *
	 * @returns `true` when it was persisted, `false` when the write failed.
	 */
	savePortfolioAmountsHidden(amountsHidden: boolean): Promise<boolean> {
		// Merged into the group rather than replacing it: the eye toggle and the
		// holdings' sort order share the one record, and a write that dropped the
		// fields it does not touch would reset the other on every toggle.
		return this.commit(
			current => ({ ...current, portfolio: { ...current.portfolio, amountsHidden } }),
			['portfolio']
		);
	}

	/**
	 * Persist the order the holdings are listed in, so the trader's last sort is
	 * still in force on their next visit — after leaving the screen, after a
	 * refresh, and after the app itself is closed.
	 *
	 * Only the portfolio record is written, as with the eye toggle above.
	 *
	 * @returns `true` when it was persisted, `false` when the write failed.
	 */
	savePortfolioSort(sortCriterion: PortfolioSortCriterion, sortDirection: PortfolioSortDirection): Promise<boolean> {
		return this.commit(
			current => ({ ...current, portfolio: { ...current.portfolio, sortCriterion, sortDirection } }),
			['portfolio']
		);
	}

	/**
	 * Persist مبنای قیمت — scenario 4: the choice applies on ذخیره, outlives the
	 * session, and comes back the same on the trader's other devices.
	 *
	 * Merged into the portfolio record rather than replacing it, as with the eye
	 * toggle and the sort above: all three share the one key.
	 *
	 * @returns `true` when it was persisted, `false` when the write failed.
	 */
	savePortfolioPriceBasis(priceBasis: PortfolioPriceBasis): Promise<boolean> {
		return this.commit(current => ({ ...current, portfolio: { ...current.portfolio, priceBasis } }), ['portfolio']);
	}

	/**
	 * Persist the symbols pinned on the market page, so they stay at the top of
	 * the list on the trader's next visit and on their other devices.
	 *
	 * Only the market record is written — the order settings, the theme and the
	 * portfolio preferences are separate keys with no stake in a pin.
	 *
	 * @returns `true` when the pins were persisted, `false` when the write failed.
	 */
	savePinnedSymbols(pinnedSymbols: readonly string[]): Promise<boolean> {
		return this.commit(
			current => ({ ...current, market: pickMarketSettings({ pinnedSymbols: [...pinnedSymbols] }) }),
			['market']
		);
	}

	/**
	 * Persist کیف پیش‌فرض, so the کیف پول tab opens on the wallet the trader chose —
	 * on the next visit, and on their other devices.
	 *
	 * Only the wallet record is written, as with the portfolio preferences above.
	 *
	 * @returns `true` when it was persisted, `false` when the write failed.
	 */
	saveDefaultWallet(defaultWalletKey: KalaWalletKey): Promise<boolean> {
		return this.commit(current => ({ ...current, wallet: { ...current.wallet, defaultWalletKey } }), ['wallet']);
	}

	/** Persist every group at once — for a full replace or a multi-group merge. */
	private commitAll(patch: SettingPatch): Promise<boolean> {
		return this.commit(patch, ALL_GROUPS);
	}

	/**
	 * The single write path: save the given records remotely, then mirror the
	 * result into the cache, application state and the event bus — never one
	 * without the others.
	 *
	 * A cache write that fails does not fail the save: the API already holds
	 * the value, so the next load recovers it.
	 *
	 * Takes a patch rather than a finished setting object because a queued write
	 * must be built from the settings as they stand when it runs: queued behind
	 * a theme save, a pin that carried its own snapshot of the other groups would
	 * publish the pre-save theme and undo it on screen.
	 *
	 * @returns `true` when the settings were persisted, `false` when the write failed.
	 */
	private commit(patch: SettingPatch, groups: readonly SettingGroup[]): Promise<boolean> {
		this.pendingWrites.update(count => count + 1);

		// A failed write must not stall the queue behind it.
		const write = this.writes.catch(() => undefined).then(() => this.runCommit(patch, groups));

		this.writes = write;

		return write.finally(() => this.pendingWrites.update(count => count - 1));
	}

	/** One write's turn at the queue: the API, then the cache and the event bus. */
	private async runCommit(patch: SettingPatch, groups: readonly SettingGroup[]): Promise<boolean> {
		const canonical = this.canonicalise(patch(this.persisted()));

		this.saveErrorMsg.set(null);

		try {
			// One group is one request; several go up as a batch, so a rejection
			// cannot leave one group saved and another not — see `saveBatch`.
			await firstValueFrom(
				groups.length === 1
					? this.userSettings.save(API_KEY_BY_GROUP[groups[0]], toRecord(canonical, groups[0]))
					: this.userSettings.saveBatch(toRecords(canonical, groups))
			);
		} catch {
			this.saveErrorMsg.set('ذخیره تنظیمات انجام نشد. دوباره تلاش کنید.');
			return false;
		}

		this.cache(canonical);
		this.publish(canonical);
		return true;
	}

	/**
	 * Promote settings to application state and announce them.
	 *
	 * Deliberately does not touch storage: the cache mirrors what the API
	 * holds, and publishing also happens for values the API has not confirmed
	 * — defaults at startup, defaults for a user with nothing stored.
	 */
	private publish(setting: KalaUserSetting): void {
		const canonical = this.canonicalise(setting);
		this.persisted.set(canonical);
		this.eventBus.dispatch(USER_SETTING_EVENTS.SETTING, canonical);
	}

	/**
	 * Mirror settings the API has confirmed into the cache.
	 *
	 * One entry per group, under the same key and holding the same record the
	 * API does, so a cached copy and the remote record are the same bytes.
	 */
	private cache(setting: KalaUserSetting): void {
		const canonical = this.canonicalise(setting);

		for (const group of ALL_GROUPS) {
			this.writeToStorage(API_KEY_BY_GROUP[group], toRecord(canonical, group));
		}
	}

	/** Fill in every group from the defaults, dropping anything we do not recognise. */
	private canonicalise(setting: Partial<KalaUserSetting>): KalaUserSetting {
		return {
			order: pickOrderSettings(setting.order),
			theme: pickTheme(setting.theme),
			portfolio: pickPortfolioSettings(setting.portfolio),
			market: pickMarketSettings(setting.market),
			general: pickGeneralSettings(setting.general),
			wallet: pickWalletSettings(setting.wallet)
		};
	}

	/**
	 * Remove the pre-per-group cache entry. Its values are not migrated: the
	 * API holds them, and the startup refresh brings them back.
	 */
	private dropLegacyCache(): void {
		try {
			localStorage.removeItem(LEGACY_KALA_USER_SETTING_STORAGE_KEY);
		} catch {
			// Storage unavailable — there is nothing to drop.
		}
	}

	/** Drop the cache: the API holds nothing for this user, so neither should we. */
	private clearCache(): void {
		try {
			for (const group of ALL_GROUPS) {
				localStorage.removeItem(API_KEY_BY_GROUP[group]);
			}
		} catch {
			// Storage unavailable — there is nothing to drop.
		}
	}

	/** Read every group back out of the cache, folding the records the same way a read does. */
	private readFromStorage(): KalaUserSetting {
		return ALL_GROUPS.reduce<KalaUserSetting>(
			(setting, group) => this.applyGroup(setting, group, this.readGroupFromStorage(group)),
			this.canonicalise({})
		);
	}

	/** @returns the cached record, or `null` when nothing usable is stored for the group. */
	private readGroupFromStorage(group: SettingGroup): unknown {
		try {
			const raw = localStorage.getItem(API_KEY_BY_GROUP[group]);
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	}

	/** @returns `true` when the write succeeded, `false` when storage is unavailable/blocked. */
	private writeToStorage(key: string, record: unknown): boolean {
		try {
			localStorage.setItem(key, JSON.stringify(record));
			return true;
		} catch {
			return false;
		}
	}
}
